import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { paymentsService } from '../../../services/paymentsService';
import { CreditCardIcon } from '@heroicons/react/24/outline';

const PaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'COMPLETED' | 'REFUNDED' | 'PARTIALLY_REFUNDED' | 'FAILED' | 'PENDING'>('all');

  const load = async () => {
    setLoading(true);
    const res = await paymentsService.history({ status: filter === 'all' ? undefined : filter, limit: 50 });
    setPayments(res.payments || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  const requestRefund = async (p: any) => {
    const within30Days = (Date.now() - new Date(p.createdAt).getTime()) <= 30 * 24 * 60 * 60 * 1000;
    if (!within30Days || p.status !== 'COMPLETED') {
      alert('Refunds are only available within 30 days for completed payments.');
      return;
    }
    if (!confirm('Request a refund for this payment?')) return;
    await paymentsService.refund(p.processorId);
    await load();
  };

  const openInvoice = (p: any) => {
    window.open(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/payments/${p.id}/invoice`, '_blank');
  };

  const downloadInvoice = async (p: any) => {
    await paymentsService.downloadInvoice(p.id);
  };

  const handleManageSubscriptions = async () => {
    setBillingPortalLoading(true);
    try {
      const response = await paymentsService.createBillingPortalSession();
      // Redirect to Stripe Billing Portal
      window.location.href = response.url;
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to open billing portal');
      setBillingPortalLoading(false);
    }
  };

  const formatAmount = (p: any) => `${p.currency} ${(p.totalAmount ?? p.amount).toFixed(2)}`;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
        <Button 
          onClick={handleManageSubscriptions}
          disabled={billingPortalLoading}
          variant="secondary"
        >
          <CreditCardIcon className="w-5 h-5 mr-2" />
          {billingPortalLoading ? 'Loading...' : 'Manage Subscriptions'}
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <select value={filter} onChange={(e)=>setFilter(e.target.value as any)} className="px-3 py-2 border rounded">
          <option value="all">All</option>
          <option value="COMPLETED">Completed</option>
          <option value="REFUNDED">Refunded</option>
          <option value="PARTIALLY_REFUNDED">Partially Refunded</option>
          <option value="FAILED">Failed</option>
          <option value="PENDING">Pending</option>
        </select>
        <Button variant="outline" onClick={load}>Refresh</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-4 py-6" colSpan={5}>Loading…</td></tr>
                ) : payments.length === 0 ? (
                  <tr><td className="px-4 py-6" colSpan={5}>No payments found.</td></tr>
                ) : payments.map(p => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 text-sm text-gray-700">{new Date(p.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{p.description || (p.membership?.tier?.name ? `Membership: ${p.membership.tier.name}` : 'Payment')}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{formatAmount(p)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        p.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                        p.status === 'REFUNDED' ? 'bg-gray-100 text-gray-700' :
                        p.status === 'PARTIALLY_REFUNDED' ? 'bg-yellow-100 text-yellow-700' :
                        p.status === 'FAILED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 space-x-2 text-right">
                      <Button size="sm" variant="outline" onClick={()=>openInvoice(p)}>View Invoice</Button>
                      <Button size="sm" onClick={()=>window.location.href = `/dashboard/payments/${p.id}`}>Details</Button>
                      <Button size="sm" variant="outline" onClick={()=>downloadInvoice(p)}>Download</Button>
                      {p.status === 'COMPLETED' && (
                        <Button size="sm" variant="danger" onClick={()=>requestRefund(p)}>Refund</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsPage;


